import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Megaphone,
  X,
  ChevronRight,
  Clock,
  ChevronDown,
  ChevronUp,
  Check,
  HelpCircle,
  AlertTriangle,
  Users,
} from "lucide-react";
import { api } from "../../lib/api";
import { useAuthStore } from "../../store/authStore";
import useTenantWebSocket from "../../hooks/useTenantWebSocket";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

/**
 * Global company announcement banner. Renders pinned channel messages at the
 * top of the AppShell.
 *
 * Rich features:
 * - Compact/expanded modes (auto-compact when ≥3 items) with localStorage.
 * - Meeting RSVP: yes/no/maybe with color-coded left border.
 * - Auto-adds accepted meetings to the user's calendar (server-side task).
 * - "Zorunlu" (mandatory) pill display.
 * - Per-user dismissal with AlertDialog confirmation.
 */

const RSVP_COLORS = {
  yes: {
    border: "border-l-emerald-500",
    ring: "ring-emerald-200",
    bg: "from-emerald-50 via-emerald-50 to-emerald-50",
  },
  no: {
    border: "border-l-red-500",
    ring: "ring-red-200",
    bg: "from-red-50 via-red-50 to-red-50",
  },
  maybe: {
    border: "border-l-amber-500",
    ring: "ring-amber-200",
    bg: "from-amber-50 via-amber-100 to-amber-50",
  },
  none: {
    border: "border-l-amber-400",
    ring: "ring-amber-200",
    bg: "from-amber-50 via-amber-100 to-amber-50",
  },
};

export default function AnnouncementBanner() {
  const { user } = useAuthStore();
  const [items, setItems] = useState([]);
  const [confirmDismissId, setConfirmDismissId] = useState(null);
  const [rsvpDetailsFor, setRsvpDetailsFor] = useState(null); // message obj
  const [rsvpDetails, setRsvpDetails] = useState(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("taskora_ann_collapsed") === "1";
  });
  const [autoCollapsed, setAutoCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return new Set(
        JSON.parse(localStorage.getItem("taskora_dismissed_ann") || "[]"),
      );
    } catch {
      return new Set();
    }
  });

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/channels/announcements");
      setItems(data || []);
    } catch {
      /* ignore */
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  // Re-check for expired announcements every minute.
  useEffect(() => {
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  useTenantWebSocket((msg) => {
    if (msg?.event === "channel.pin" || msg?.event === "channel.rsvp") load();
  });

  const persistDismissed = (next) => {
    setDismissed(next);
    localStorage.setItem(
      "taskora_dismissed_ann",
      JSON.stringify(Array.from(next)),
    );
  };

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    setAutoCollapsed(false);
    localStorage.setItem("taskora_ann_collapsed", next ? "1" : "0");
  };

  const nowMs = Date.now();
  const visible = useMemo(
    () =>
      items.filter((i) => {
        if (dismissed.has(i.id)) return false;
        if (i.expires_at) {
          const t = new Date(i.expires_at).getTime();
          if (!isNaN(t) && t <= nowMs) return false;
        }
        return true;
      }),
    [items, dismissed, nowMs],
  );

  // Auto-collapse the banner when there are ≥3 unread announcements, but
  // only once per session (the user can still open/close via the toggle).
  useEffect(() => {
    if (visible.length >= 3 && !autoCollapsed && !collapsed) {
      const remembered = localStorage.getItem("taskora_ann_collapsed");
      if (remembered === null) {
        setCollapsed(true);
        setAutoCollapsed(true);
      }
    }
  }, [visible.length, autoCollapsed, collapsed]);

  if (visible.length === 0) return null;

  const openChannel = (item) => {
    window.dispatchEvent(
      new CustomEvent("dm-open", {
        detail: { kind: "channel", id: item.channel_id, name: item.channel_name },
      }),
    );
  };

  const confirmHide = () => {
    if (!confirmDismissId) return;
    persistDismissed(new Set([...dismissed, confirmDismissId]));
    setConfirmDismissId(null);
  };

  const formatExpiry = (iso) => {
    try {
      return new Date(iso).toLocaleString("tr-TR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const renderBodyWithLinks = (text) => {
    const parts = text.split(/(https?:\/\/[^\s]+)/g);
    return parts.map((p, i) =>
      /^https?:\/\//.test(p) ? (
        <a
          key={i}
          href={p}
          target="_blank"
          rel="noreferrer"
          className="font-medium underline decoration-current/60 underline-offset-2 hover:opacity-80"
          onClick={(e) => e.stopPropagation()}
        >
          {p}
        </a>
      ) : (
        <span key={i}>{p}</span>
      ),
    );
  };

  const meetingOf = (it) => it.meeting && it.meeting.is_meeting ? it.meeting : null;

  const canSeeAttendees = (it) =>
    !!meetingOf(it) &&
    (it.sender_id === user?.id || ["super_admin", "ultra_admin"].includes(user?.role));

  const openRsvpDetails = async (item) => {
    setRsvpDetailsFor(item);
    setRsvpDetails(null);
    setRsvpLoading(true);
    try {
      const { data } = await api.get(`/channels/messages/${item.id}/rsvps`);
      setRsvpDetails(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Katılım listesi alınamadı");
      setRsvpDetailsFor(null);
    } finally {
      setRsvpLoading(false);
    }
  };

  const sendRsvp = async (item, status) => {
    try {
      const { data } = await api.post(
        `/channels/messages/${item.id}/rsvp`,
        { status },
      );
      // Optimistically patch local state.
      setItems((prev) =>
        prev.map((x) =>
          x.id === item.id
            ? {
                ...x,
                my_rsvp: status,
                rsvps: { ...(x.rsvps || {}), [user.id]: status },
              }
            : x,
        ),
      );      if (status === "yes") {
        toast.success(
          data.task_id
            ? "Katılım onaylandı — takviminize eklendi ✅"
            : "Katılım onaylandı ✅",
        );
      } else if (status === "no") {
        toast("Katılmayacağın kaydedildi", { icon: "❌" });
      } else {
        toast("Belki olarak işaretlendi", { icon: "🤔" });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const requiredCount = visible.filter(
    (i) => meetingOf(i)?.required,
  ).length;
  const pendingRsvpCount = visible.filter(
    (i) => meetingOf(i) && !i.my_rsvp,
  ).length;

  // ---------- Compact strip ----------
  if (collapsed) {
    return (
      <div
        data-testid="announcement-strip"
        className="flex items-center gap-3 border-b border-amber-300 bg-gradient-to-r from-amber-50 via-amber-100 to-amber-50 px-4 py-1.5"
      >
        <Megaphone className="h-4 w-4 shrink-0 text-amber-700" strokeWidth={2} />
        <span className="text-xs font-semibold uppercase tracking-wider text-amber-900">
          {visible.length} aktif duyuru
        </span>
        {requiredCount > 0 && (
          <span
            data-testid="ann-required-count"
            className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700"
          >
            <AlertTriangle className="h-3 w-3" /> {requiredCount} zorunlu
          </span>
        )}
        {pendingRsvpCount > 0 && (
          <span
            data-testid="ann-pending-count"
            className="inline-flex items-center gap-1 rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-900"
          >
            <HelpCircle className="h-3 w-3" /> {pendingRsvpCount} yanıt bekliyor
          </span>
        )}
        <button
          type="button"
          onClick={toggleCollapsed}
          data-testid="ann-expand"
          className="ml-auto flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-200"
        >
          Tümünü göster <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  // ---------- Expanded list ----------
  return (
    <>
      <div
        data-testid="announcement-banner"
        className="max-h-72 overflow-y-auto border-b border-amber-300 bg-gradient-to-r from-amber-50 via-amber-100 to-amber-50"
      >
        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-amber-200/70 bg-amber-100/70 px-4 py-1 backdrop-blur">
          <Megaphone className="h-3.5 w-3.5 text-amber-700" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-900">
            {visible.length} aktif duyuru
          </span>
          <button
            type="button"
            onClick={toggleCollapsed}
            data-testid="ann-collapse"
            className="ml-auto flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium text-amber-900 hover:bg-amber-200"
          >
            Daralt <ChevronUp className="h-3 w-3" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-px bg-amber-200/70 md:grid-cols-2">
          {visible.map((it) => {
            const meeting = meetingOf(it);
            const my = it.my_rsvp;
            const palette = RSVP_COLORS[my || "none"];
            return (
              <div
                key={it.id}
                data-testid={`announcement-${it.id}`}
                className={`border-l-4 ${palette.border} bg-gradient-to-r ${palette.bg} px-4 py-2`}
              >
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">
                        Duyuru · #{it.channel_name || "kanal"}
                        {it.sender_name && ` · ${it.sender_name}`}
                      </span>
                      {meeting?.required && (
                        <span
                          data-testid={`ann-required-${it.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-700 ring-1 ring-red-200"
                        >
                          <AlertTriangle className="h-3 w-3" /> Zorunlu
                        </span>
                      )}
                      {it.expires_at && (
                        <span
                          data-testid={`ann-expires-${it.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-amber-200/70 px-2 py-0.5 text-[10px] font-medium text-amber-900"
                        >
                          <Clock className="h-3 w-3" />
                          {formatExpiry(it.expires_at)}
                        </span>
                      )}
                      {it.rsvp_counts && meeting && (
                        <span className="ml-1 text-[10px] text-zinc-600">
                          ✅ {it.rsvp_counts.yes || 0} · ❌ {it.rsvp_counts.no || 0} · 🤔 {it.rsvp_counts.maybe || 0}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 whitespace-pre-wrap break-words text-sm text-amber-950">
                      {renderBodyWithLinks((it.body || "").slice(0, 1200))}
                    </div>

                    {meeting && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => sendRsvp(it, "yes")}
                          data-testid={`rsvp-yes-${it.id}`}
                          className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition ${
                            my === "yes"
                              ? "bg-emerald-600 text-white shadow"
                              : "bg-white text-emerald-700 ring-1 ring-emerald-300 hover:bg-emerald-50"
                          }`}
                        >
                          <Check className="h-3.5 w-3.5" /> Katılacağım
                        </button>
                        <button
                          type="button"
                          onClick={() => sendRsvp(it, "no")}
                          data-testid={`rsvp-no-${it.id}`}
                          className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition ${
                            my === "no"
                              ? "bg-red-600 text-white shadow"
                              : "bg-white text-red-700 ring-1 ring-red-300 hover:bg-red-50"
                          }`}
                        >
                          <X className="h-3.5 w-3.5" /> Katılmayacağım
                        </button>
                        <button
                          type="button"
                          onClick={() => sendRsvp(it, "maybe")}
                          data-testid={`rsvp-maybe-${it.id}`}
                          className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition ${
                            my === "maybe"
                              ? "bg-amber-500 text-white shadow"
                              : "bg-white text-amber-700 ring-1 ring-amber-300 hover:bg-amber-50"
                          }`}
                        >
                          <HelpCircle className="h-3.5 w-3.5" /> Belki
                        </button>
                        {my === "yes" && (
                          <span
                            data-testid={`rsvp-calendar-${it.id}`}
                            className="text-[10px] text-emerald-700"
                          >
                            📅 Takvimine eklendi
                          </span>
                        )}
                        {canSeeAttendees(it) && (
                          <button
                            type="button"
                            onClick={() => openRsvpDetails(it)}
                            data-testid={`rsvp-details-${it.id}`}
                            className="ml-auto inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-medium text-zinc-700 ring-1 ring-zinc-300 hover:bg-zinc-50"
                            title="Katılım listesini görüntüle (sadece organizatör)"
                          >
                            <Users className="h-3.5 w-3.5" />
                            Kimler katılıyor?
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex shrink-0 items-start gap-1">
                    <button
                      type="button"
                      onClick={() => openChannel(it)}
                      data-testid={`ann-open-${it.id}`}
                      className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-200"
                    >
                      Kanala git <ChevronRight className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDismissId(it.id)}
                      data-testid={`ann-dismiss-${it.id}`}
                      className="rounded p-1 text-amber-800 hover:bg-amber-200"
                      aria-label="Kapat"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <AlertDialog
        open={!!confirmDismissId}
        onOpenChange={(o) => !o && setConfirmDismissId(null)}
      >
        <AlertDialogContent data-testid="ann-dismiss-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Emin misin?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu duyuru bannerini yalnızca senin için gizleyeceğiz. Duyuru
              başkalarında görünmeye devam edecek. Devam etmek istiyor musun?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ann-dismiss-cancel">
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmHide}
              data-testid="ann-dismiss-ok"
              className="bg-amber-600 hover:bg-amber-700"
            >
              Evet, gizle
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!rsvpDetailsFor}
        onOpenChange={(o) => !o && setRsvpDetailsFor(null)}
      >
        <DialogContent
          className="sm:max-w-lg max-h-[85vh] overflow-y-auto"
          data-testid="rsvp-details-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-amber-600" /> Toplantı Katılım Listesi
            </DialogTitle>
            <DialogDescription>
              {rsvpDetailsFor?.body?.split("\n").find((l) => l.trim() && !l.startsWith("📅") && !l.startsWith("🔴") && !l.startsWith("🕒") && !l.startsWith("💻") && !l.startsWith("🔗") && !l.startsWith("📍") && !l.startsWith("📌")) || "Toplantı"}
            </DialogDescription>
          </DialogHeader>

          {rsvpLoading && (
            <div className="py-8 text-center text-sm text-zinc-500">
              Yükleniyor...
            </div>
          )}

          {!rsvpLoading && rsvpDetails && (
            <div className="space-y-4">
              <RsvpGroup
                testid="rsvp-list-yes"
                label="Katılacak"
                emoji="✅"
                accent="text-emerald-700 bg-emerald-50 ring-emerald-200"
                users={rsvpDetails.yes}
              />
              <RsvpGroup
                testid="rsvp-list-no"
                label="Katılmayacak"
                emoji="❌"
                accent="text-red-700 bg-red-50 ring-red-200"
                users={rsvpDetails.no}
              />
              <RsvpGroup
                testid="rsvp-list-maybe"
                label="Belki"
                emoji="🤔"
                accent="text-amber-700 bg-amber-50 ring-amber-200"
                users={rsvpDetails.maybe}
              />
              <RsvpGroup
                testid="rsvp-list-pending"
                label="Henüz yanıt vermedi"
                emoji="⏳"
                accent="text-zinc-600 bg-zinc-50 ring-zinc-200"
                users={rsvpDetails.pending}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function RsvpGroup({ testid, label, emoji, accent, users }) {
  return (
    <div data-testid={testid} className={`rounded-lg ring-1 ${accent} p-3`}>
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <span>{emoji}</span>
        <span>{label}</span>
        <span className="ml-auto rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-medium">
          {users?.length || 0}
        </span>
      </div>
      {(!users || users.length === 0) ? (
        <p className="text-xs text-zinc-500">— kimse yok —</p>
      ) : (
        <ul className="space-y-1">
          {users.map((u) => (
            <li
              key={u.user_id}
              className="flex items-center gap-2 rounded-md bg-white/70 px-2 py-1.5 text-xs"
            >
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-[10px] font-semibold text-zinc-700">
                {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
              </div>
              <span className="min-w-0 truncate">
                <span className="font-medium">{u.name || u.email}</span>
                {u.name && u.email && (
                  <span className="ml-1 text-zinc-500">· {u.email}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

